import { useState, useEffect, useCallback, useRef } from "react";
import { passwordsAPI } from "../lib/api";
import { CATEGORIES, getAvatarGradient } from "../lib/passwordUtils";
import { useLang } from "../lib/i18n";
import PasswordModal from "../components/PasswordModal";
import Header from "../components/Header";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Search, Plus, FolderOpen, Globe, MessageCircle, Landmark, Mail, Briefcase, Archive, Copy, Pencil, Trash2, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

var iconMap = { FolderOpen:FolderOpen, Globe:Globe, MessageCircle:MessageCircle, Landmark:Landmark, Mail:Mail, Briefcase:Briefcase, Archive:Archive };

export default function DashboardPage({ user, setUser, onLogout, onAdmin, onUpgrade }) {
  var { t } = useLang();
  var [passwords, setPasswords] = useState([]);
  var [filtered, setFiltered] = useState([]);
  var [search, setSearch] = useState("");
  var [category, setCategory] = useState("all");
  var [sortBy, setSortBy] = useState("title");
  var [modalOpen, setModalOpen] = useState(false);
  var [editEntry, setEditEntry] = useState(null);
  var [loading, setLoading] = useState(true);
  var [offline, setOffline] = useState(false);
  var printRef = useRef(null);

  var fetchPasswords = useCallback(async function() {
    try {
      var res = await passwordsAPI.getAll();
      setPasswords(res.data);
      if (res.offline) { setOffline(true); toast.warning(t.offlineMode); } else { setOffline(false); }
    } catch(e) { toast.error(t.connectionError); } finally { setLoading(false); }
  }, [t]);

  useEffect(function() { fetchPasswords(); }, [fetchPasswords]);

  useEffect(function() {
    var items = passwords.slice();
    if (category !== "all") items = items.filter(function(p) { return p.category === category; });
    if (search) { var q = search.toLowerCase(); items = items.filter(function(p) { return p.title.toLowerCase().includes(q) || (p.username && p.username.toLowerCase().includes(q)); }); }
    items.sort(function(a, b) { return sortBy === "title" ? a.title.localeCompare(b.title) : new Date(b.updated_at) - new Date(a.updated_at); });
    setFiltered(items);
  }, [passwords, search, category, sortBy]);

  async function handleCopy(text, label) { try { await navigator.clipboard.writeText(text); toast.success(t.copy); } catch(e) {} }

  async function handleDelete(entry) {
    if (!window.confirm(t.confirmDelete + ' "' + entry.title + '"?')) return;
    try { await passwordsAPI.delete(entry.id); setPasswords(function(prev) { return prev.filter(function(p) { return p.id !== entry.id; }); }); toast.success(t.passwordDeleted); } catch(e) { toast.error("Error"); }
  }

  async function handleSave(data, id) {
    try {
      if (id) { var res = await passwordsAPI.update(id, data); setPasswords(function(prev) { return prev.map(function(p) { return p.id===id ? res.data : p; }); }); toast.success(t.passwordUpdated); }
      else { var res2 = await passwordsAPI.create(data); setPasswords(function(prev) { return prev.concat([res2.data]); }); toast.success(t.passwordAdded); }
      setModalOpen(false); setEditEntry(null);
    } catch (err) {
      if (err.response?.status === 403) { toast.error(t.freeLimit); onUpgrade(); }
      else { toast.error("Error"); }
    }
  }

  function handleAdd() {
    if (user.plan === "free" && user.email !== "acaridi57@gmail.com" && passwords.length >= 10) {
      toast.error(t.freeLimit); onUpgrade(); return;
    }
    setEditEntry(null); setModalOpen(true);
  }

  function doBackup(pwds, silent) {
    var data = pwds.map(function(p) { return { title:p.title, username:p.username, password:p.password, url:p.url||"", category:p.category, notes:p.notes||"" }; });
    var now = new Date(); var fn = "backup_password_" + now.toISOString().slice(0,10) + ".json";
    var json = JSON.stringify({ exported_at: now.toISOString(), total: data.length, passwords: data }, null, 2);
    var blob = new Blob([json], { type: "application/json" });
    if (window.showSaveFilePicker) {
      window.showSaveFilePicker({ suggestedName: fn, types: [{ description: "JSON", accept: { "application/json": [".json"] } }] })
        .then(function(h) { return h.createWritable().then(function(w) { w.write(blob).then(function() { w.close(); if(!silent) toast.success(t.backupSaved); }); }); })
        .catch(function() {});
    } else {
      var u = URL.createObjectURL(blob); var a = document.createElement("a"); a.href = u; a.download = fn; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u); if(!silent) toast.success(t.backupDownloaded);
    }
  }

  function handleRestore() {
    var input = document.createElement("input"); input.type = "file"; input.accept = ".json";
    input.onchange = async function(e) {
      var file = e.target.files[0]; if (!file) return;
      try {
        var text = await file.text(); var data = JSON.parse(text); var items = data.passwords || data;
        if (!Array.isArray(items) || items.length === 0) { toast.error(t.invalidFile); return; }
        if (!window.confirm(t.importConfirm.replace("{count}", items.length))) return;
        var imported = 0;
        for (var i = 0; i < items.length; i++) {
          if (!items[i].title || !items[i].password) continue;
          try { await passwordsAPI.create({ title:items[i].title, username:items[i].username||"", password:items[i].password, url:items[i].url||"", category:items[i].category||"altro", notes:items[i].notes||"" }); imported++; } catch(err) {}
        }
        toast.success(imported + " " + t.importSuccess); fetchPasswords();
      } catch(err) { toast.error(t.invalidFile); }
    };
    input.click();
  }

  function handleLogout() { if (passwords.length > 0) doBackup(passwords, true); onLogout(); }

  var catCounts = passwords.reduce(function(acc, p) { acc[p.category] = (acc[p.category]||0)+1; return acc; }, {});

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC]" data-testid="dashboard-page">
      <Header user={user} onLogout={handleLogout} onAdmin={onAdmin} onAdd={handleAdd} onPrint={function(){window.print();}} onBackup={function(){doBackup(passwords,false);}} onRestore={handleRestore} onUpgrade={onUpgrade} />

      {offline && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-center py-2 text-sm font-medium no-print" data-testid="offline-banner">
          {t.offlineMode} <button onClick={function(){setLoading(true);setOffline(false);fetchPasswords();}} className="ml-3 underline font-bold">{t.retryConnection}</button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-52 shrink-0 bg-white border-r border-slate-200 p-4 hidden md:flex flex-col gap-1 no-print" data-testid="category-sidebar">
          {CATEGORIES.map(function(cat) {
            var Icon = iconMap[cat.icon]; var count = cat.key === "all" ? passwords.length : (catCounts[cat.key]||0);
            return (
              <button key={cat.key} data-testid={"cat-"+cat.key} onClick={function(){setCategory(cat.key);}}
                className={"flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all text-left " + (category===cat.key ? "bg-green-50 text-green-700 border-l-[3px] border-green-700 pl-3" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700")}>
                <Icon className="w-4 h-4 shrink-0" /><span className="flex-1">{cat.label}</span><span className="text-xs text-slate-400">{count}</span>
              </button>
            );
          })}
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white shrink-0 no-print">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-slate-800">{t.yourPasswords}</h2>
              <span className="text-xs text-slate-400" data-testid="entry-count">{filtered.length} {t.entries}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="search-input" value={search} onChange={function(e){setSearch(e.target.value);}} placeholder={t.search} className="pl-9 w-64 h-9 bg-slate-50 border-slate-200 text-slate-800 rounded-full text-sm" />
              </div>
              <select data-testid="sort-select" value={sortBy} onChange={function(e){setSortBy(e.target.value);}} className="h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 text-sm outline-none cursor-pointer">
                <option data-testid="sort-title-option" value="title" label={String(t.sortAZ || "A-Z")} /><option data-testid="sort-date-option" value="date" label={String(t.sortRecent || "Recenti")} />
              </select>
              <Button data-testid="add-password-mobile" onClick={handleAdd} size="sm" className="md:hidden bg-gradient-to-r from-green-700 to-green-800 text-white">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-6 space-y-2.5 no-print">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <div className="w-8 h-8 border-2 border-green-700 border-t-transparent rounded-full animate-spin mb-4" />{t.loading}
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="empty-state">
                  <KeyRound className="w-16 h-16 mb-4 opacity-30" /><p>{t.noPasswords}</p>
                  <Button onClick={handleAdd} className="mt-4 bg-gradient-to-r from-green-700 to-green-800 text-white"><Plus className="w-4 h-4 mr-2" /> {t.addFirst}</Button>
                </div>
              ) : filtered.map(function(entry) {
                return <PasswordCard key={entry.id} entry={entry} t={t} onCopy={handleCopy} onEdit={function(){setEditEntry(entry);setModalOpen(true);}} onDelete={function(){handleDelete(entry);}} />;
              })}
            </div>
            <div className="hidden print:block print-area p-6">
              <h1 className="text-xl font-bold mb-4">{t.yourPasswords} - {new Date().toLocaleDateString("it-IT")}</h1>
              {passwords.sort(function(a,b){return a.title.localeCompare(b.title);}).map(function(entry) {
                return <div key={entry.id} className="print-card"><strong>{entry.title}</strong>{entry.username && <div>User: {entry.username}</div>}<div>Password: {entry.password}</div>{entry.notes && <div>Note: {entry.notes}</div>}</div>;
              })}
            </div>
          </ScrollArea>
        </main>
      </div>
      <PasswordModal open={modalOpen} onClose={function(){setModalOpen(false);setEditEntry(null);}} onSave={handleSave} entry={editEntry} />
    </div>
  );
}

function PasswordCard({ entry, t, onCopy, onEdit, onDelete }) {
  var [showPwd, setShowPwd] = useState(false);
  var grad = getAvatarGradient(entry.title);
  var initials = entry.title.substring(0, 2).toUpperCase();
  return (
    <div data-testid={"password-card-"+entry.id} className="group flex items-center gap-4 px-5 py-4 bg-white border border-slate-200 rounded-xl transition-all hover:shadow-md hover:translate-x-0.5">
      <div className={"w-11 h-11 rounded-xl bg-gradient-to-br "+grad+" flex items-center justify-center text-white font-bold text-sm shrink-0"}>{initials}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-800 truncate">{entry.title}</div>
        <div className="text-xs text-slate-400 truncate mt-0.5">{entry.username || "N/A"}</div>
      </div>
      <div className="flex items-center gap-2 min-w-0 max-w-[200px]">
        <code className="text-xs text-slate-500 font-mono truncate bg-slate-50 px-2 py-1 rounded border border-slate-100">{showPwd ? entry.password : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}</code>
        <button data-testid={"toggle-pwd-"+entry.id} onClick={function(){setShowPwd(!showPwd);}} className="text-slate-400 hover:text-slate-600 shrink-0 text-sm">{showPwd ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
      </div>
      <span className="hidden lg:block text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-1 shrink-0">{entry.category}</span>
      <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        {entry.username && <button data-testid={"copy-user-"+entry.id} onClick={function(){onCopy(entry.username,"Username");}} className="w-8 h-8 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:bg-green-700 hover:border-green-700 hover:text-white transition-all" title="Copy username"><Copy className="w-3.5 h-3.5" /></button>}
        <button data-testid={"copy-pwd-"+entry.id} onClick={function(){onCopy(entry.password,"Password");}} className="w-8 h-8 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:bg-green-700 hover:border-green-700 hover:text-white transition-all" title="Copy password"><KeyRound className="w-3.5 h-3.5" /></button>
        <button data-testid={"edit-pwd-"+entry.id} onClick={onEdit} className="w-8 h-8 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:bg-green-700 hover:border-green-700 hover:text-white transition-all" title={t.edit}><Pencil className="w-3.5 h-3.5" /></button>
        <button data-testid={"delete-pwd-"+entry.id} onClick={onDelete} className="w-8 h-8 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:bg-red-500 hover:border-red-500 hover:text-white transition-all" title={t.delete}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}
